import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, mergeMap, Subject, takeUntil } from 'rxjs';

import { ClientService } from '@app/core/services/client.service';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { NotificationService } from '@app/core/services/notification.service';
import { Client } from '@app/core/types/client';
import {
  KYC_STATE_STYLE_MAP,
  KycState,
  KycStates,
} from '@app/core/types/kyc-state';
import { HttpErrorResponse } from '@angular/common/http';

interface KycStateView {
  name: KycStates;
  icon: string;
  color: string;
}

@Component({
  selector: 'itfg-kyc-state',
  templateUrl: './kyc-state.component.html',
  styleUrl: './kyc-state.component.scss',
  standalone: false,
})
export class KycStateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() client!: Client;
  @Input() compactView = true;
  @Input() displayLabel = false;

  public STYLE_MAP = KYC_STATE_STYLE_MAP;

  public currentState: KycStates | null = null;
  public kycStateViews: KycStateView[] = [];
  public KycStates: typeof KycStates = KycStates;
  public unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public dialogProvider: DialogProviderService,
    public clientService: ClientService,
    public notificationService: NotificationService,
    public translate: TranslateService,
    public viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {
    this.clientService.kycStates$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((states) => {
        this.kycStateViews = states.map((s: KycState) => ({
          name: s.name as KycStates,
          icon: this.STYLE_MAP[s.name]?.icon ?? 'shield_question',
          color: this.STYLE_MAP[s.name]?.backgroundColor ?? '#9e9e9e',
        }));
      });

    this.clientService.currentClient$
      .pipe(
        filter((c): c is Client => !!c && c.id === this.client?.id),
        takeUntil(this.unsubscribe)
      )
      .subscribe((client: Client) => {
        this.client = client;
        this.currentState = (client.kycState?.name as KycStates) ?? null;
      });
  }

  ngOnChanges(): void {
    this.currentState = (this.client?.kycState?.name as KycStates) ?? null;
  }

  toggleKycState(): void {
    if (
      this.currentState !== KycStates.VALID &&
      this.currentState !== KycStates.INVALID
    ) {
      return;
    }

    const goingToValid = this.currentState === KycStates.INVALID;
    const newStateKey = goingToValid ? KycStates.VALID : KycStates.INVALID;

    const config = {
      message: this.translate.instant(
        'verification.kycState.toggleConfirmMessage',
        {
          from: this.translate.instant(
            `verification.kycState.short.${this.currentState}`
          ),
          to: this.translate.instant(
            `verification.kycState.short.${newStateKey}`
          ),
        }
      ),
      disableClose: false,
      viewContainerRef: this.viewContainerRef,
      title: this.translate.instant('verification.kycState.toggleConfirmTitle'),
      acceptButton: this.translate.instant('global.confirm'),
      cancelButton: this.translate.instant('global.cancel'),
    };

    const request$ = goingToValid
      ? this.clientService.validateClientKycState(this.client.id)
      : this.clientService.invalidateClientKycState(this.client.id);

    this.dialogProvider
      .openConfirm(config)
      .afterClosed()
      .pipe(
        filter((accept) => accept === true),
        mergeMap(() => request$),
        mergeMap(() => this.clientService.getClientById(this.client.id)),
        takeUntil(this.unsubscribe)
      )
      .subscribe({
        next: (client: Client) => {
          this.clientService.setCurrentClient(client);
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'verification.kycState.updateSuccess',
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.kycState.updateError',
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
